/** Reimport tab — replay THIS request's stored webhook body through its
 *  integration (the single-row edition of the grid's batch Reimport; it
 *  submits the same batch action with just this id).
 *
 *  Unavailable for requests without an integration — there is nothing to
 *  route the replay through.
 */
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { ApiRequestDetail, ReimportParams } from '@/api/apiRequests';
import { runBatch } from '@/api/batch';
import { ConsoleApiError } from '@/api/client';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { openBackgroundTask } from '@/features/bgtasks/openBackgroundTask';

import { reimportIntegrationActions, type ApiRequestCtx } from '../apiRequestLinks';

/** Same as the grids' batch snackbar (`useBatchAction`): long enough to
 *  click the View task button. */
const QUEUED_AUTOHIDE_MS = 15_000;

const NOTIFICATION_CHOICES: { value: ReimportParams['send_notifications']; label: string }[] = [
  { value: 'default', label: "Integration's own config" },
  { value: 'send', label: 'Force ON for this replay' },
  { value: 'skip', label: 'Force OFF for this replay' },
];

export function ReimportTab({ detail }: { detail: ApiRequestDetail }) {
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const ctx: ApiRequestCtx = {
    tenantId: detail.tenant?.id ?? null,
    createdTs: detail.created_ts,
  };

  const [sendNotifications, setSendNotifications] =
    useState<ReimportParams['send_notifications']>('default');
  const [includePast, setIncludePast] = useState(true);
  const [ignoreIfFutureUpdates, setIgnoreIfFutureUpdates] = useState(false);
  const [force, setForce] = useState(false);

  const mutation = useMutation({
    mutationFn: () => {
      const params: ReimportParams = {
        send_notifications: sendNotifications,
        include_past: includePast,
        ignore_if_future_updates: ignoreIfFutureUpdates,
        force,
      };
      return runBatch('api_requests', 'reimport', { mode: 'ids', ids: [detail.id] }, { ...params });
    },
    onSuccess: (res) => {
      snackbar.show(`Reimport queued as background task ${res.task_id}.`, 'success', {
        autoHideDuration: QUEUED_AUTOHIDE_MS,
        action: { label: 'View task', onClick: () => openBackgroundTask(res.task_id) },
      });
      void queryClient.invalidateQueries({ queryKey: ['api-requests'] });
    },
    onError: (e) => {
      const message = e instanceof ConsoleApiError ? e.message : 'Reimport failed.';
      snackbar.show(`Reimport failed: ${message}`, 'error');
    },
  });

  if (!detail.integration || detail.integration.toLowerCase() === 'none') {
    return (
      <Alert severity="info">
        This request has no integration attached — there is nothing to replay it through.
        Reimport is only available for integration webhooks.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, flex: 1 }}>
      <Alert severity="warning">
        Reimporting replays this request&apos;s stored webhook body through the{' '}
        <ValueActions actions={reimportIntegrationActions(detail.integration, ctx)}>
          <strong>{detail.integration}</strong>
        </ValueActions>{' '}
        integration — bookings are re-created/updated as
        if the webhook arrived again. Runs in the background; a failure stamps an error series
        onto the request.
      </Alert>

      <FormControl size="small" sx={{ maxWidth: 420 }}>
        <InputLabel id="reimport-tab-notifications-label">Guest notifications</InputLabel>
        <Select
          labelId="reimport-tab-notifications-label"
          label="Guest notifications"
          value={sendNotifications}
          onChange={(e) =>
            setSendNotifications(e.target.value as ReimportParams['send_notifications'])
          }
        >
          {NOTIFICATION_CHOICES.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          Whether changes made by the replay send guest invitations/notifications.
        </FormHelperText>
      </FormControl>

      <Stack spacing={0.5}>
        <FormControlLabel
          control={
            <Switch checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} />
          }
          label="Reimport even if all its bookings are in the past"
        />
        <FormControlLabel
          control={
            <Switch
              checked={ignoreIfFutureUpdates}
              onChange={(e) => setIgnoreIfFutureUpdates(e.target.checked)}
            />
          }
          label="Skip if a newer request exists for the same booking"
        />
        <FormControlLabel
          control={<Switch checked={force} onChange={(e) => setForce(e.target.checked)} />}
          label="Force processing (bypass the integration's skip heuristics)"
        />
      </Stack>

      <Box>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ReplayIcon />}
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Reimport now
        </Button>
      </Box>
    </Box>
  );
}
